import { mentionRegex } from '../../modules/home/v1/components/chat-input/constants';

export const BOLD_REGEX = /\*(\S.*?\S|\S)\*/g;

export const ITALIC_REGEX = /_((?![^>]*<\/a>)(?![^<]*<\/a>)(?:\S.*?\S|\S))_/g;

export const STRIKE_REGEX = /~(\S.*?\S|\S)~/g;

export const CODE_REGEX = /```([^\s].*?)```/g;

export const NEW_LINE_REGEX = /\r\n|\n/g;

export const REGEX_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

/*
  this regex have issue
  it will match URL but also matches text in URL
  so will give wrong output when we split using it
*/
export const LINK_REGEX =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g;

/**
 * this regex will match URL but not text in URL
 */
export const LINK_REGEX_2 = /\b((?:https?|ftp):\/\/[^\s/$.?#].[^\s]*)|((?:www|ftp)\.[^\s/$.?#].[^\s]*)/gi;

export const REGEX_MAP = [
  { regex: BOLD_REGEX, replaceWith: '<b>$1</b>' },
  { regex: ITALIC_REGEX, replaceWith: '<i>$1</i>' },
  { regex: STRIKE_REGEX, replaceWith: '<del>$1</del>' },
  { regex: CODE_REGEX, replaceWith: '<code>$1</code>' },
  { regex: NEW_LINE_REGEX, replaceWith: '<br />' },
];

interface MentionReplacement {
  id: string;
  name: string;
}

export const formatNoteMentions = (noteText: string, members: MentionReplacement[] = []) => {
  return members.reduce((formattedNoteText, member) => {
    const escapedMemberId = member.id.replace(REGEX_SPECIAL_CHARS, '\\$&');
    const regex = new RegExp(`@\\[\\{${escapedMemberId}\\}\\]`, 'g');

    return formattedNoteText.replace(regex, `@${member.name}`);
  }, noteText);
};

export const injectMentionStyles = () => {
  if (document.getElementById('mention-text-styles')) return;

  const style = document.createElement('style');
  style.id = 'mention-text-styles';
  style.setAttribute('nonce', 'desktop-css');

  style.textContent = `
    .mention-text {
      color: #3f906d !important;
      font-weight: 500 !important;
      background-color: #3f906d1a !important;
      padding: 0 2px;
      border-radius: 4px;
    }
  `;

  document.head.appendChild(style);
};

const formatMentions = (message: string, formatMentionStyle: string) => {
  injectMentionStyles();
  return message.replace(mentionRegex, `<span class="mention-text">@$1</span>`);
};

const formatTextSegment = (text: string) => {
  return REGEX_MAP.reduce((acc, { regex, replaceWith }) => {
    return acc.replace(regex, replaceWith);
  }, text);
};

export const formatText = (
  text: string,
  formatMention: boolean = false,
  formatMentionStyle: string = 'color: #3f906d; font-weight: 500; background-color: #3f906d1a;'
) => {
  const segments = text.split(LINK_REGEX_2);
  const formattedSegments = segments.map((segment) => {
    if (segment && segment.match(LINK_REGEX_2)) {
      if (!segment.startsWith('https://') && !segment.startsWith('http://')) {
        segment = `https://${segment}`;
      }
      return `<a href="${segment}" target="_blank" rel="noopener noreferrer">${segment}</a>`;
    }
    return segment ? formatTextSegment(segment) : '';
  });

  let formattedText = formatMention
    ? formatMentions(formattedSegments.join(''), formatMentionStyle)
    : formattedSegments.join('');

  return formattedText;
};

const injectHighlightStyles = () => {
  if (document.getElementById('highlight-text-styles')) return;

  const style = document.createElement('style');
  style.id = 'highlight-text-styles';
  style.setAttribute('nonce', 'desktop-css');
  style.textContent = `
    .highlight-text {
      color: #3f906d !important;
      font-weight: 700 !important;
    }
  `;
  document.head.appendChild(style);
};

const getUrlRanges = (text: string): Array<[number, number]> => {
  const urlRanges: Array<[number, number]> = [];
  const urlRegex = new RegExp(LINK_REGEX_2.source, 'gi');
  let urlMatch: RegExpExecArray | null;

  while ((urlMatch = urlRegex.exec(text)) !== null) {
    const start = urlMatch.index ?? 0;
    const end = start + (urlMatch[0]?.length ?? 0);
    if (end > start) urlRanges.push([start, end]);
    // Safety against zero-length matches
    if (urlRegex.lastIndex === start) urlRegex.lastIndex++;
  }

  return urlRanges;
};

function highlightSearchText(text: string, search: string) {
  if (!search) return text;

  const normalizedText = text.replace(/(\r\n|\n|\r)/gm, ' ');
  const TRIM_OFFSET = 12;

  // Escape regex special characters and create search pattern
  const searchWords = search
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(REGEX_SPECIAL_CHARS, '\\$&'));

  if (searchWords.length === 0) return normalizedText;

  // Create regex to find all matches (without word boundaries to allow partial word matches)
  const regex = new RegExp(`(${searchWords.join('|')})`, 'gi');

  // Find first match index
  const firstMatch = regex.exec(normalizedText);
  if (!firstMatch) return normalizedText;

  const firstMatchIndex = firstMatch.index;

  // Reset regex lastIndex for use in replace() later
  regex.lastIndex = 0;

  // Trim text before first match if needed
  const processedText =
    firstMatchIndex > TRIM_OFFSET ? '...' + normalizedText.slice(firstMatchIndex - TRIM_OFFSET) : normalizedText;

  // Inject highlight styles once
  injectHighlightStyles();

  // Precompute URL ranges to avoid highlighting inside URLs
  const urlRanges = getUrlRanges(processedText);

  // Check if offset is inside any URL range
  const isOffsetInsideUrl = (offset: number) => urlRanges.some(([start, end]) => offset >= start && offset < end);

  // Replace matches with highlighted spans, skipping matches inside URLs
  return processedText.replace(regex, (matchedText, _group1, offset) => {
    if (typeof offset === 'number' && isOffsetInsideUrl(offset)) {
      return matchedText;
    }
    return `<span class="highlight-text">${matchedText}</span>`;
  });
}

export const formatSearchText = (text: string, string: string) => {
  const result = highlightSearchText(text, string);
  return formatText(result);
};

export default formatText;
